import React from 'react';
import type { PillarIcon } from './types';

// Pillar icons exported from Figma (Dosen 3.0, node 13227:33042).
// Lucide-style 2px stroke; viewBox matches the source SVG so the icon
// sits centred in a 24×24 container with ~1px breathing room.

interface IconProps {
  size?: number;
  color?: string;
}

// Onboard — sitemap (3 squares + connectors).
export const OnboardIcon: React.FC<IconProps> = ({ size = 24, color = '#FDFDFD' }) => (
  <svg width={size} height={size} viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4 15V12C4 11.7348 4.10536 11.4804 4.29289 11.2929C4.48043 11.1054 4.73478 11 5 11H17C17.2652 11 17.5196 11.1054 17.7071 11.2929C17.8946 11.4804 18 11.7348 18 12V15M11 11V7M16 15H20C20.5523 15 21 15.4477 21 16V20C21 20.5523 20.5523 21 20 21H16C15.4477 21 15 20.5523 15 20V16C15 15.4477 15.4477 15 16 15ZM2 15H6C6.55228 15 7 15.4477 7 16V20C7 20.5523 6.55228 21 6 21H2C1.44772 21 1 20.5523 1 20V16C1 15.4477 1.44772 15 2 15ZM9 1H13C13.5523 1 14 1.44772 14 2V6C14 6.55228 13.5523 7 13 7H9C8.44772 7 8 6.55228 8 6V2C8 1.44772 8.44772 1 9 1Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Operate — two people / users.
export const OperateIcon: React.FC<IconProps> = ({ size = 24, color = '#FDFDFD' }) => (
  <svg width={size} height={size} viewBox="0 0 22 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M15 19V17C15 15.9391 14.5786 14.9217 13.8284 14.1716C13.0783 13.4214 12.0609 13 11 13H5C3.93913 13 2.92172 13.4214 2.17157 14.1716C1.42143 14.9217 1 15.9391 1 17V19M21 18.9999V16.9999C20.9993 16.1136 20.7044 15.2527 20.1614 14.5522C19.6184 13.8517 18.8581 13.3515 18 13.1299M15 1.12988C15.8604 1.35018 16.623 1.85058 17.1676 2.55219C17.7122 3.2538 18.0078 4.11671 18.0078 5.00488C18.0078 5.89305 17.7122 6.75596 17.1676 7.45757C16.623 8.15918 15.8604 8.65958 15 8.87988M12 5C12 7.20914 10.2091 9 8 9C5.79086 9 4 7.20914 4 5C4 2.79086 5.79086 1 8 1C10.2091 1 12 2.79086 12 5Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Perform — building / document outline.
export const PerformIcon: React.FC<IconProps> = ({ size = 24, color = '#FDFDFD' }) => (
  <svg width={size} height={size} viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M5 21V3C5 2.46957 5.21071 1.96086 5.58579 1.58579C5.96086 1.21071 6.46957 1 7 1H15C15.5304 1 16.0391 1.21071 16.4142 1.58579C16.7893 1.96086 17 2.46957 17 3V21M5 21H17M5 21H3C2.46957 21 1.96086 20.7893 1.58579 20.4142C1.21071 20.0391 1 19.5304 1 19V13C1 12.4696 1.21071 11.9609 1.58579 11.5858C1.96086 11.2107 2.46957 11 3 11H5M17 21H19C19.5304 21 20.0391 20.7893 20.4142 20.4142C20.7893 20.0391 21 19.5304 21 19V10C21 9.46957 20.7893 8.96086 20.4142 8.58579C20.0391 8.21071 19.5304 8 19 8H17M9 5H13M9 9H13M9 13H13M9 17H13"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Lead — server stack (two horizontal rectangles).
export const LeadIcon: React.FC<IconProps> = ({ size = 24, color = '#FDFDFD' }) => (
  <svg width={size} height={size} viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M5 17H7M11 17H17M6 1H16C17.1046 1 18 1.89543 18 3V7C18 8.10457 17.1046 9 16 9H6C4.89543 9 4 8.10457 4 7V3C4 1.89543 4.89543 1 6 1ZM3 13H19C20.1046 13 21 13.8954 21 15V19C21 20.1046 20.1046 21 19 21H3C1.89543 21 1 20.1046 1 19V15C1 13.8954 1.89543 13 3 13Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const PILLAR_ICON_FOR_KEY: Record<PillarIcon, React.FC<IconProps>> = {
  onboard: OnboardIcon,
  operate: OperateIcon,
  perform: PerformIcon,
  lead: LeadIcon
};
